import type { ComponentType, SVGProps } from 'react';
import {
  Bell, Blocks, Briefcase, Building2, Calendar, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Circle, CircleAlert, CircleUser, Contact, Download, ExternalLink,
  Eye, EyeOff, Factory, Info, File, FilePlus, FileSignature, FileText, Folder, GripVertical, Handshake, Landmark, LayoutDashboard, Library, Lock, LockOpen, LogOut, Mail, MapPin, Menu, Minus,
  Package, PanelLeftClose, PanelLeftOpen, Pencil, Phone, Plus, Printer, Receipt, RefreshCw, Search, Settings, ShieldAlert, ShieldCheck, Smartphone, Star, Target, TriangleAlert, User, Users, Wallet,
  WifiOff, Wrench, X,
  AlignCenter, AlignJustify, AlignLeft, AlignRight, Baseline, Bold, Braces, Heading, Highlighter, Image, Italic, List, ListOrdered, Strikethrough, Underline,
  ClipboardList, ClipboardCheck, Percent, Scale, Truck, Undo2,
  Banknote, SquarePen,
  CircleHelp,
  FileQuestionMark, ListTree,
  FileMinus, HandCoins, BanknoteArrowUp,
  Boxes,
  Route, ScrollText, Siren, HardHat, CalendarRange, CalendarCheck, PhoneCall,
  History, MessageSquare, PhoneIncoming, PhoneOutgoing, Play, Voicemail,
  BookOpen, GraduationCap, TableOfContents,
  BetweenHorizontalEnd, BetweenVerticalEnd, FoldHorizontal, FoldVertical, IndentDecrease, IndentIncrease, Link, PanelTop, SeparatorHorizontal, Table,
  Forward, MessageCircle, Reply, ReplyAll,
  FileArchive, FileImage, FileSpreadsheet, FileType,
  Copy,
  Archive, ClockArrowUp, FilePen, Inbox, Mailbox, Send, ShieldX, Trash2,
  ArrowUp, Flag, MailOpen, Paperclip,
  Ellipsis,
  Share2,
  ChartBarStacked, ChartLine, FileChartColumn, FileChartLine, FileChartPie, TrendingUp,
  AlarmClock, Award, Bookmark, Compass, Cpu, Flame, Gauge, Gem, Globe, Layers, Leaf, Map as MapGlyph, Megaphone, Tag, Thermometer, Zap,
  Cog, Component, Warehouse, Workflow,
} from 'lucide-react';
import { filledMeanings, iconReference, type AppGlyphName, type IconMeaning, type IconName, type LucideIconName } from '@bms2/shared';
import { appGlyphs } from './appGlyphs';
import styles from './Icon.module.css';

/**
 * The one way an icon is drawn (piece 1). A caller names a meaning from the
 * shared icon reference and a size context; the component resolves the Lucide
 * glyph. There is no name prop and no stroke prop: the same meaning always
 * draws the same icon, and stroke width tracks size at 1/12 because Lucide
 * draws a 2-unit stroke in a 24-unit box and the box scales. 🔒 A meaning no
 * Lucide icon carries draws the application's own inline glyph through this
 * same control (decision 3011): a file row's file-type icon.
 */
export type IconSize = 'nav' | 'inline' | 'header' | 'banner';

type Glyph = ComponentType<SVGProps<SVGSVGElement>>;

/** Lucide's component for each icon name in the reference. */
const glyphs: Record<LucideIconName, Glyph> = {
  copy: Copy,
  'file-chart-pie': FileChartPie,
  'chart-line': ChartLine,
  'file-chart-column': FileChartColumn,
  'chart-bar-stacked': ChartBarStacked,
  'file-chart-line': FileChartLine,
  'trending-up': TrendingUp,
  // Decision 2260: the sixteen tag category glyphs, `tag` among them for a tag with no category.
  award: Award,
  layers: Layers,
  cpu: Cpu,
  map: MapGlyph,
  megaphone: Megaphone,
  gauge: Gauge,
  'alarm-clock': AlarmClock,
  tag: Tag,
  zap: Zap,
  globe: Globe,
  thermometer: Thermometer,
  flame: Flame,
  leaf: Leaf,
  gem: Gem,
  bookmark: Bookmark,
  compass: Compass,
  ellipsis: Ellipsis,
  paperclip: Paperclip,
  flag: Flag,
  'arrow-up': ArrowUp,
  'mail-open': MailOpen,
  mailbox: Mailbox,
  inbox: Inbox,
  'file-pen': FilePen,
  send: Send,
  'clock-arrow-up': ClockArrowUp,
  archive: Archive,
  'shield-x': ShieldX,
  'trash-2': Trash2,
  circle: Circle,
  'building-2': Building2,
  user: User,
  factory: Factory,
  package: Package,
  handshake: Handshake,
  target: Target,
  calendar: Calendar,
  check: Check,
  minus: Minus,
  lock: Lock,
  x: X,
  pencil: Pencil,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'external-link': ExternalLink,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'file-plus': FilePlus,
  'file-text': FileText,
  'file-question-mark': FileQuestionMark,
  'file-minus': FileMinus,
  'hand-coins': HandCoins,
  'banknote-arrow-up': BanknoteArrowUp,
  'list-tree': ListTree,
  wallet: Wallet,
  landmark: Landmark,
  settings: Settings,
  menu: Menu,
  'circle-user': CircleUser,
  'log-out': LogOut,
  'layout-dashboard': LayoutDashboard,
  'panel-left-close': PanelLeftClose,
  'panel-left-open': PanelLeftOpen,
  'shield-alert': ShieldAlert,
  'wifi-off': WifiOff,
  wrench: Wrench,
  boxes: Boxes,
  warehouse: Warehouse,
  workflow: Workflow,
  component: Component,
  cog: Cog,
  route: Route,
  'scroll-text': ScrollText,
  siren: Siren,
  'hard-hat': HardHat,
  'calendar-range': CalendarRange,
  'calendar-check': CalendarCheck,
  'phone-call': PhoneCall,
  history: History,
  'message-square': MessageSquare,
  'phone-incoming': PhoneIncoming,
  'phone-outgoing': PhoneOutgoing,
  voicemail: Voicemail,
  play: Play,
  'book-open': BookOpen,
  'table-of-contents': TableOfContents,
  'graduation-cap': GraduationCap,
  star: Star,
  'triangle-alert': TriangleAlert,
  'circle-alert': CircleAlert,
  info: Info,
  eye: Eye,
  'eye-off': EyeOff,
  'grip-vertical': GripVertical,
  'lock-open': LockOpen,
  heading: Heading,
  bold: Bold,
  italic: Italic,
  underline: Underline,
  strikethrough: Strikethrough,
  list: List,
  'list-ordered': ListOrdered,
  'align-left': AlignLeft,
  'align-center': AlignCenter,
  'align-right': AlignRight,
  'align-justify': AlignJustify,
  image: Image,
  baseline: Baseline,
  highlighter: Highlighter,
  braces: Braces,
  // The article editor's word-processor set (decision 0800).
  'indent-increase': IndentIncrease,
  'indent-decrease': IndentDecrease,
  link: Link,
  'separator-horizontal': SeparatorHorizontal,
  table: Table,
  'between-horizontal-end': BetweenHorizontalEnd,
  'fold-vertical': FoldVertical,
  'between-vertical-end': BetweenVerticalEnd,
  'fold-horizontal': FoldHorizontal,
  'panel-top': PanelTop,
  // Piece 22's purchase and stock side: a vendor's invoice, a credit back, a count, a move between sites and a change in value.
  'undo-2': Undo2,
  'clipboard-list': ClipboardList,
  'clipboard-check': ClipboardCheck,
  truck: Truck,
  scale: Scale,
  percent: Percent,
  // Piece 24: the banking side's own glyphs — the accounts, the payment run and the checks.
  banknote: Banknote,
  'square-pen': SquarePen,
  file: File,
  'map-pin': MapPin,
  download: Download,
  printer: Printer,
  'refresh-cw': RefreshCw,
  plus: Plus,
  search: Search,
  users: Users,
  contact: Contact,
  folder: Folder,
  'shield-check': ShieldCheck,
  library: Library,
  'file-signature': FileSignature,
  receipt: Receipt,
  'circle-help': CircleHelp,
  briefcase: Briefcase,
  mail: Mail,
  bell: Bell,
  smartphone: Smartphone,
  phone: Phone,
  blocks: Blocks,
  reply: Reply,
  'reply-all': ReplyAll,
  'message-circle': MessageCircle,
  forward: Forward,
  'file-image': FileImage,
  'file-spreadsheet': FileSpreadsheet,
  'file-type': FileType,
  'file-archive': FileArchive,
  // Decision 3013: Share on the narrow file row.
  'share-2': Share2,
};

/** 🔒 Every name the reference resolves to, a Lucide icon or the application's own glyph (decision 3011). */
const drawn: Record<IconName, Glyph> = { ...glyphs, ...(appGlyphs satisfies Record<AppGlyphName, Glyph>) };

export interface IconProps {
  meaning: IconMeaning;
  size?: IconSize;
  /** Accessible name when the icon carries meaning on its own; omit for a decorative icon. */
  label?: string;
}

export const iconDocumentedStates = ['nav', 'inline', 'header', 'banner', 'filled'] as const;

/**
 * 🔒 A meaning this build does not know draws an empty box of the same size and
 * never throws (decision 3030). A meaning reaches the browser from the server's
 * own rows, a navigation row's glyph among them, and a deploy's new API seeds its
 * rows a minute or more before the new web build serves the page. 💰 The tab held
 * across run 1741's swap drew React's error 130: the old build met the new seed's
 * Service Reports row, whose meaning it had no glyph for, and the whole frame went
 * with it. The box keeps the row's alignment until the next load draws the glyph.
 */
export function Icon({ meaning, size = 'inline', label }: IconProps) {
  const name = iconReference[meaning] as IconName | undefined;
  const Glyph = name === undefined ? undefined : drawn[name];
  const filled = filledMeanings.has(meaning);
  const className = [styles.icon, styles[size], filled ? styles.filled : ''].filter(Boolean).join(' ');
  if (Glyph === undefined) {
    return (
      <svg
        className={className}
        viewBox="0 0 24 24"
        data-icon-unknown="true"
        data-meaning={meaning}
        aria-hidden={label ? undefined : true}
        role={label ? 'img' : undefined}
        aria-label={label}
        focusable="false"
      />
    );
  }
  return (
    <Glyph
      className={className}
      data-icon={name}
      data-meaning={meaning}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      focusable="false"
    />
  );
}
